import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

const COLORS = { bg: "#F4F7FA", navy: "#003E62", cyan: "#008AB0", text: "#1F2A33", muted: "#5A6B78", danger: "#B23A3A", ok: "#2E8B4D" };
const LOGO_SRC = "/marca/mpires-connect-cor-petroleo.png";
const BARLOW = "'Barlow', system-ui, -apple-system, sans-serif";
const SEM = "sem_contato";

function Progress({ step, total }) {
  const pct = Math.min(100, Math.round(((step - 1) / total) * 100));
  return (
    <div style={{ height: 6, background: "#DCE6EE", borderRadius: 999, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ width: `${pct}%`, height: "100%", background: COLORS.cyan, transition: "width 300ms" }} />
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontWeight: 700, color: COLORS.navy, fontSize: 15, marginBottom: 6 }}>
        {label}{required && <span style={{ color: COLORS.danger }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = { width: "100%", padding: "12px 14px", fontSize: 16, fontFamily: BARLOW, border: "1.5px solid #CFD8DF", borderRadius: 10, background: "#fff", color: COLORS.text, outline: "none", boxSizing: "border-box" };

function Btn({ children, onClick, disabled, variant = "primary", testid, type = "button" }) {
  const base = { width: "100%", padding: "14px 16px", fontFamily: BARLOW, fontWeight: 800, fontSize: 17, borderRadius: 12, border: 0, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1, marginTop: 10 };
  const styles = { primary: { ...base, background: COLORS.navy, color: "#fff" }, ghost: { ...base, background: "transparent", color: COLORS.navy, border: `1.5px solid ${COLORS.navy}` } };
  return <button type={type} onClick={onClick} disabled={disabled} style={styles[variant]} data-testid={testid}>{children}</button>;
}

function NotaRadio({ value, onChange, name, allowSemContato = false, testidBase }) {
  const marked = (n) => value === n;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <label key={n} data-testid={testidBase ? `${testidBase}-${n}` : undefined} style={{
          flex: "1 1 48px", minWidth: 46, textAlign: "center", padding: "10px 0",
          borderRadius: 10, border: `1.5px solid ${marked(n) ? COLORS.navy : "#CFD8DF"}`,
          background: marked(n) ? COLORS.navy : "#fff",
          color: marked(n) ? "#fff" : COLORS.navy, fontWeight: 700, cursor: "pointer", fontSize: 16,
        }}>
          <input type="radio" name={name} value={n} checked={marked(n)} onChange={() => onChange(n)} style={{ display: "none" }} />{n}
        </label>
      ))}
      {allowSemContato && (
        <label data-testid={testidBase ? `${testidBase}-sem` : undefined} style={{
          flex: "1 1 100%", textAlign: "center", padding: "10px 0",
          borderRadius: 10, border: `1.5px solid ${value === SEM ? COLORS.navy : "#CFD8DF"}`,
          background: value === SEM ? COLORS.navy : "#fff",
          color: value === SEM ? "#fff" : COLORS.navy, fontWeight: 700, cursor: "pointer", fontSize: 14, marginTop: 4,
        }}>
          <input type="radio" name={name} value={SEM} checked={value === SEM} onChange={() => onChange(SEM)} style={{ display: "none" }} />
          Não tive contato
        </label>
      )}
    </div>
  );
}

function formatarDataBR(iso) {
  if (!iso) return "";
  try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
}

export default function CanalClienteQ() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [treinamento, setTreinamento] = useState(null);
  const [aplicacao, setAplicacao] = useState(null);
  const [step, setStep] = useState(1);
  const [ident, setIdent] = useState({ nome: "", setor: "", funcao: "" });
  const [respostas, setRespostas] = useState({});
  const [av, setAv] = useState({
    nota_geral: 0, clareza: 0, aplicavel: 0, dominio_instrutor: 0,
    nota_atendimento_geral: null, nota_atendimento_tecnico: null,
    nota_atendimento_vendedor: null, nota_atendimento_gerente: null,
  });
  const [comentarios, setComentarios] = useState({ aproveitou: "", melhoria: "" });
  const [novoTema, setNovoTema] = useState({ quer: "", tema: "" });
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const total = 4;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/canal-cliente/public/${token}`);
        setTreinamento(data?.treinamento);
        setAplicacao(data?.aplicacao || null);
      } catch { setErro("Link indisponível"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const questoes = useMemo(() => treinamento?.questoes || [], [treinamento]);

  const avancar1 = () => {
    if (!ident.setor.trim()) { alert("Informe o setor."); return; }
    if (!ident.funcao) { alert("Selecione sua função."); return; }
    setStep(2);
  };
  const avancar2 = () => {
    for (const q of questoes) if (!respostas[q.id]) { alert("Responda todas as questões."); return; }
    setStep(3);
  };
  const enviar = async () => {
    for (const k of ["nota_geral", "clareza", "aplicavel", "dominio_instrutor"]) {
      if (!av[k]) { alert("Dê todas as notas do treinamento (1 a 5)."); return; }
    }
    for (const k of ["nota_atendimento_geral", "nota_atendimento_tecnico", "nota_atendimento_vendedor", "nota_atendimento_gerente"]) {
      if (av[k] === null || av[k] === undefined || av[k] === "") { alert("Avalie o atendimento (ou marque 'Não tive contato')."); return; }
    }
    setEnviando(true);
    try {
      const { data } = await api.post(`/canal-cliente/public/${token}/resposta`, {
        identificacao: ident, respostas, avaliacao: av, comentarios, novo_tema: novoTema,
      });
      setResultado(data); setStep(4);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Não foi possível enviar.";
      alert(msg);
    } finally { setEnviando(false); }
  };
  const voltar = () => setStep((s) => Math.max(1, s - 1));

  const container = { minHeight: "100vh", background: COLORS.bg, fontFamily: BARLOW, color: COLORS.text, padding: "20px 16px 40px" };
  const card = { maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: 16, padding: 20, boxShadow: "0 2px 12px rgba(0,20,35,0.06)" };

  if (loading) return <div style={container}><div style={card}><p style={{ textAlign: "center", color: COLORS.muted }}>Carregando…</p></div></div>;
  if (erro || !treinamento) return (
    <div style={container}><div style={card}>
      <img src={LOGO_SRC} alt="MPires" style={{ height: 48, display: "block", margin: "0 auto 16px" }} />
      <h1 style={{ color: COLORS.navy, textAlign: "center", fontSize: 22, margin: 0 }}>Link indisponível</h1>
      <p style={{ color: COLORS.muted, textAlign: "center", marginTop: 10 }}>Peça um QR válido ao representante da MPires.</p>
    </div></div>
  );
  if (aplicacao?.encerrada) return (
    <div style={container}><div style={card} data-testid="canal-encerrada">
      <img src={LOGO_SRC} alt="MPires" style={{ height: 48, display: "block", margin: "0 auto 16px" }} />
      <h1 style={{ color: COLORS.navy, textAlign: "center", fontSize: 22, margin: 0 }}>Avaliação encerrada. Obrigado!</h1>
      <p style={{ color: COLORS.muted, textAlign: "center", marginTop: 10 }}>O prazo para responder esta avaliação terminou.</p>
    </div></div>
  );

  return (
    <div style={container}>
      <div style={card} data-testid="canal-cliente-page">
        <img src={LOGO_SRC} alt="MPires" style={{ height: 44, display: "block", margin: "0 auto 10px" }} />
        <h1 style={{ color: COLORS.navy, textAlign: "center", fontSize: 20, margin: "0 0 4px", fontWeight: 800 }}>Avaliação de treinamento</h1>
        {aplicacao ? (
          <p style={{ color: COLORS.muted, textAlign: "center", margin: "0 0 18px", fontSize: 14 }}>
            <strong style={{ color: COLORS.navy }}>{aplicacao.tema}</strong>
            {aplicacao.data && <> — {formatarDataBR(aplicacao.data)}</>}
          </p>
        ) : (
          <p style={{ color: COLORS.muted, textAlign: "center", margin: "0 0 18px", fontSize: 14 }}>{treinamento.titulo}</p>
        )}
        {step < 4 && <Progress step={step} total={total} />}

        {step === 1 && (
          <div data-testid="canal-tela-1">
            <Field label="Nome (opcional)">
              <input style={inputStyle} value={ident.nome} onChange={(e) => setIdent({ ...ident, nome: e.target.value })} data-testid="canal-nome" />
            </Field>
            <Field label="Setor" required>
              <input style={inputStyle} value={ident.setor} onChange={(e) => setIdent({ ...ident, setor: e.target.value })} data-testid="canal-setor" />
            </Field>
            <Field label="Sua função" required>
              <select style={inputStyle} value={ident.funcao} onChange={(e) => setIdent({ ...ident, funcao: e.target.value })} data-testid="canal-funcao">
                <option value="">Selecione…</option>
                <option>Operacional</option>
                <option>Supervisão ou gestão</option>
              </select>
            </Field>
            <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 8 }}>Seus dados são usados apenas para registro do treinamento.</p>
            <Btn onClick={avancar1} testid="canal-btn-avancar-1">Começar</Btn>
          </div>
        )}

        {step === 2 && (
          <div data-testid="canal-tela-2">
            {questoes.map((q, i) => (
              <div key={q.id} style={{ marginBottom: 18 }}>
                <p style={{ fontWeight: 700, color: COLORS.navy, fontSize: 15, margin: "0 0 8px" }}>{i + 1}. {q.enunciado}</p>
                {q.alternativas.map((a) => (
                  <label key={a.id} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, marginBottom: 6,
                    border: `1.5px solid ${respostas[q.id] === a.id ? COLORS.navy : "#CFD8DF"}`,
                    background: respostas[q.id] === a.id ? "#E8F2F6" : "#fff", cursor: "pointer",
                  }}>
                    <input type="radio" name={q.id} value={a.id} checked={respostas[q.id] === a.id}
                      onChange={() => setRespostas({ ...respostas, [q.id]: a.id })} data-testid={`canal-q-${q.id}-${a.id}`} />
                    <span style={{ fontSize: 15 }}>{a.texto}</span>
                  </label>
                ))}
              </div>
            ))}
            <Btn onClick={avancar2} testid="canal-btn-avancar-2">Continuar</Btn>
            <Btn onClick={voltar} variant="ghost" testid="canal-btn-voltar-2">Voltar</Btn>
          </div>
        )}

        {step === 3 && (
          <div data-testid="canal-tela-3">
            <p style={{ color: COLORS.navy, fontWeight: 800, fontSize: 14, letterSpacing: 0.5, marginTop: 4, marginBottom: 8, textTransform: "uppercase" }}>Treinamento</p>
            <Field label="Nota geral do treinamento" required><NotaRadio name="nota_geral" value={av.nota_geral} onChange={(n) => setAv({ ...av, nota_geral: n })} testidBase="nota-geral" /></Field>
            <Field label="O conteúdo foi claro?" required><NotaRadio name="clareza" value={av.clareza} onChange={(n) => setAv({ ...av, clareza: n })} testidBase="nota-clareza" /></Field>
            <Field label="Consegue aplicar no dia a dia?" required><NotaRadio name="aplicavel" value={av.aplicavel} onChange={(n) => setAv({ ...av, aplicavel: n })} testidBase="nota-aplicavel" /></Field>
            <Field label="Domínio do instrutor" required><NotaRadio name="dominio_instrutor" value={av.dominio_instrutor} onChange={(n) => setAv({ ...av, dominio_instrutor: n })} testidBase="nota-dominio" /></Field>
            <p style={{ color: COLORS.navy, fontWeight: 800, fontSize: 14, letterSpacing: 0.5, marginTop: 16, marginBottom: 8, textTransform: "uppercase" }}>Atendimento MPires</p>
            <Field label="Atendimento MPires em geral" required><NotaRadio name="at_geral" value={av.nota_atendimento_geral} onChange={(n) => setAv({ ...av, nota_atendimento_geral: n })} allowSemContato testidBase="nota-at-geral" /></Field>
            <Field label="Atendimento técnico MPires" required><NotaRadio name="at_tecnico" value={av.nota_atendimento_tecnico} onChange={(n) => setAv({ ...av, nota_atendimento_tecnico: n })} allowSemContato testidBase="nota-at-tecnico" /></Field>
            <Field label="Atendimento do vendedor" required><NotaRadio name="at_vendedor" value={av.nota_atendimento_vendedor} onChange={(n) => setAv({ ...av, nota_atendimento_vendedor: n })} allowSemContato testidBase="nota-at-vendedor" /></Field>
            <Field label="Atendimento do gerente" required><NotaRadio name="at_gerente" value={av.nota_atendimento_gerente} onChange={(n) => setAv({ ...av, nota_atendimento_gerente: n })} allowSemContato testidBase="nota-at-gerente" /></Field>
            <Field label="O que você mais aproveitou?">
              <textarea style={{ ...inputStyle, minHeight: 70 }} value={comentarios.aproveitou} onChange={(e) => setComentarios({ ...comentarios, aproveitou: e.target.value })} data-testid="canal-coment-bom" />
            </Field>
            <Field label="O que você melhoraria?">
              <textarea style={{ ...inputStyle, minHeight: 70 }} value={comentarios.melhoria} onChange={(e) => setComentarios({ ...comentarios, melhoria: e.target.value })} data-testid="canal-coment-melhoria" />
            </Field>
            <Field label="Quer treinamento sobre outro tema?">
              <select style={inputStyle} value={novoTema.quer} onChange={(e) => setNovoTema({ quer: e.target.value, tema: e.target.value === "Outro" ? "" : e.target.value })} data-testid="canal-novo-tema">
                <option value="">Selecione…</option>
                <option>Não</option>
                <option>Lavanderia</option>
                <option>Cozinha</option>
                <option>Uso de EPI</option>
                <option value="Outro">Outro</option>
              </select>
            </Field>
            {novoTema.quer === "Outro" && (
              <Field label="Qual tema?">
                <input style={inputStyle} value={novoTema.tema} onChange={(e) => setNovoTema({ ...novoTema, tema: e.target.value })} data-testid="canal-novo-tema-outro" />
              </Field>
            )}
            <Btn onClick={enviar} disabled={enviando} testid="canal-btn-enviar">{enviando ? "Enviando…" : "Enviar"}</Btn>
            <Btn onClick={voltar} variant="ghost" testid="canal-btn-voltar-3">Voltar</Btn>
          </div>
        )}

        {step === 4 && resultado && (
          <div data-testid="canal-tela-4">
            <p style={{ textAlign: "center", color: COLORS.navy, fontWeight: 800, fontSize: 22, margin: "8px 0 4px" }}>
              Você acertou {resultado.acertos} de {resultado.total}
            </p>
            <p style={{ textAlign: "center", color: COLORS.muted, marginTop: 0 }}>Veja o gabarito abaixo:</p>
            {questoes.map((q, i) => {
              const gab = (resultado.gabarito || []).find((g) => g.id === q.id);
              const minhaResp = respostas[q.id];
              const acertou = gab?.resposta === minhaResp;
              const altCorreta = q.alternativas.find((a) => a.id === gab?.resposta);
              return (
                <div key={q.id} style={{ padding: 12, borderRadius: 10, border: `1.5px solid ${acertou ? COLORS.ok : COLORS.danger}`, marginBottom: 10 }}>
                  <p style={{ fontWeight: 700, color: COLORS.navy, margin: "0 0 6px", fontSize: 15 }}>{i + 1}. {q.enunciado}</p>
                  <p style={{ margin: "4px 0", fontSize: 14 }}><strong>Resposta certa:</strong> {altCorreta?.texto || "—"}</p>
                  <p style={{ margin: "4px 0", fontSize: 14, color: COLORS.muted }}>{gab?.explicacao}</p>
                </div>
              );
            })}
            <p style={{ textAlign: "center", color: COLORS.navy, fontWeight: 700, marginTop: 20 }}>Obrigado pela avaliação.</p>
          </div>
        )}
      </div>
    </div>
  );
}
